/* eslint-disable newline-per-chained-call */
import { z } from 'zod';
import { isAwarenessCampaignCtaOption } from '../constants/awareness-cta-options';
import { isSalesCampaignCtaOption } from '../constants/sales-cta-options';

// Helper function to validate URL format
const isValidUrl = (urlString: string): boolean => {
  try {
    return Boolean(new URL(urlString));
  } catch {
    // Try with https prefix
    return false;
  }
};

// Reusable URL schema that auto-normalizes URLs by adding https:// if missing
const urlSchema = z.preprocess(
  (val) => {
    if (!val || typeof val !== 'string') return val;
    const trimmed = val.trim();
    if (!trimmed) return trimmed;
    // Add https:// if no protocol is present
    if (!trimmed.match(/^https?:\/\//)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  },
  z.string().refine(
    (val) => {
      if (!val) return false; // Don't allow empty strings - they will be validated in superRefine
      return isValidUrl(val);
    },
    { message: 'Please enter a valid URL for the Call To Action.' },
  ),
);

// Define the base schema for new ad set (old schema - without conversion location)
const adSetNewBaseOld = z.object({
  type: z.literal('new'),
  name: z.string().min(2, { message: 'Ad set name must be at least 2 characters.' }),
  optimizationGoal: z
    .enum([
      'REACH',
      'IMPRESSIONS',
      'AD_RECALL_LIFT',
      'LINK_CLICKS',
      'LANDING_PAGE_VIEWS',
      'POST_ENGAGEMENT',
      'EVENT_RESPONSES',
      'VIDEO_VIEWS',
      'LEAD_GENERATION',
      'OFFSITE_CONVERSIONS',
      'APP_INSTALLS',
    ])
    .optional(),
  billingEvent: z
    .enum([
      'APP_INSTALLS',
      'CLICKS',
      'IMPRESSIONS',
      'LINK_CLICKS',
      'LISTING_INTERACTION',
      'OFFER_CLAIMS',
      'PAGE_LIKES',
      'POST_ENGAGEMENT',
      'PURCHASE',
      'THRUPLAY',
      'VIDEO_VIEWS',
    ])
    .optional(),
  bidAmount: z.number().int().min(0).max(1000000).optional(),
  dailyBudget: z.number().positive().max(1000000).optional(),
  lifetimeBudget: z.number().positive().max(100000000).optional(),
  startTime: z.preprocess(
    (val) => (val ? new Date(val as string) : undefined),
    z
      .date()
      .refine((d) => !isNaN(d.getTime()), { message: 'Start time is not a valid date' })
      .optional(),
  ),
  stopTime: z.preprocess(
    (val) => (val ? new Date(val as string) : undefined),
    z
      .date()
      .refine((d) => !isNaN(d.getTime()), { message: 'Stop time is not a valid date' })
      .optional()
      .refine((val) => !val || val > new Date(), {
        message: 'Stop time must be null or a date in the future',
      }),
  ),
  targeting: z.object({
    geo_locations: z.object({
      countries: z.array(z.string()).optional(),
    }),
  }),
  status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
  // Conversion tracking fields
  promotedObject: z
    .object({
      pixelId: z.string().optional(),
      customEventType: z.string().optional(),
      appId: z.string().optional(),
      customConversionId: z.string().optional(),
    })
    .optional(),
  attributionSpec: z
    .array(
      z.object({
        eventType: z.enum(['CLICK', 'VIEW']),
        windowDays: z.number().int().min(1).max(30), // Changed from enum to integer (1-30 days)
      }),
    )
    .optional(),
});

// Define the base schema for new ad set (new schema - with conversion location)
const adSetNewBaseNew = z.object({
  type: z.literal('new'),
  name: z.string().min(2, { message: 'Ad set name must be at least 2 characters.' }),
  // EPD-2014: Add conversion location field for Sales campaigns
  conversionLocation: z.enum(['website']).default('website'),
  optimizationGoal: z
    .enum([
      'REACH',
      'IMPRESSIONS',
      'AD_RECALL_LIFT',
      'LINK_CLICKS',
      'LANDING_PAGE_VIEWS',
      'POST_ENGAGEMENT',
      'EVENT_RESPONSES',
      'VIDEO_VIEWS',
      'LEAD_GENERATION',
      'OFFSITE_CONVERSIONS',
      'APP_INSTALLS',
    ])
    .optional(),
  billingEvent: z
    .enum([
      'APP_INSTALLS',
      'CLICKS',
      'IMPRESSIONS',
      'LINK_CLICKS',
      'LISTING_INTERACTION',
      'OFFER_CLAIMS',
      'PAGE_LIKES',
      'POST_ENGAGEMENT',
      'PURCHASE',
      'THRUPLAY',
      'VIDEO_VIEWS',
    ])
    .optional(),
  bidAmount: z.number().int().min(0).max(1000000).optional(),
  dailyBudget: z.number().positive().max(1000000).optional(),
  lifetimeBudget: z.number().positive().max(100000000).optional(),
  startTime: z.preprocess(
    (val) => (val ? new Date(val as string) : undefined),
    z
      .date()
      .refine((d) => !isNaN(d.getTime()), { message: 'Start time is not a valid date' })
      .optional(),
  ),
  stopTime: z.preprocess(
    (val) => (val ? new Date(val as string) : undefined),
    z
      .date()
      .refine((d) => !isNaN(d.getTime()), { message: 'Stop time is not a valid date' })
      .optional()
      .refine((val) => !val || val > new Date(), {
        message: 'Stop time must be null or a date in the future',
      }),
  ),
  targeting: z.object({
    geo_locations: z.object({
      countries: z.array(z.string()).optional(),
    }),
  }),
  status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
  // Conversion tracking fields
  promotedObject: z
    .object({
      pixelId: z.string().optional(),
      customEventType: z.string().optional(),
      appId: z.string().optional(),
      customConversionId: z.string().optional(),
    })
    .optional(),
  attributionSpec: z
    .array(
      z.object({
        eventType: z.enum(['CLICK', 'VIEW']),
        windowDays: z.number().int().min(1).max(30), // Changed from enum to integer (1-30 days)
      }),
    )
    .optional(),
});

// Campaign objectives for old schema (only AWARENESS)
const campaignObjectivesOld = z.enum(['OUTCOME_AWARENESS']);

// Campaign objectives for new schema (all objectives)
const campaignObjectivesNew = z.enum([
  'OUTCOME_APP_PROMOTION',
  'OUTCOME_AWARENESS',
  'OUTCOME_ENGAGEMENT',
  'OUTCOME_LEADS',
  'OUTCOME_SALES',
  'OUTCOME_TRAFFIC',
]);

// Create schema factory function that takes feature flag as parameter
export const createBoostPostSchema = (conversionObjectivesEnabled: boolean) => {
  // Select appropriate base schemas and objectives based on feature flag
  const adSetNewBase = conversionObjectivesEnabled ? adSetNewBaseNew : adSetNewBaseOld;
  const campaignObjectives = conversionObjectivesEnabled ? campaignObjectivesNew : campaignObjectivesOld;

  return z
    .object({
      adAccount: z.object({
        id: z.string().min(1, { message: 'Please select an ad account.' }),
      }),
      post: z.object({
        postExternalId: z.string().min(1, { message: 'Please select a post.' }),
        platform: z.literal('instagram'),
      }),
      campaign: z.discriminatedUnion('type', [
        z.object({
          type: z.literal('existing'),
          campaignId: z.string().min(1, { message: 'Please select a campaign.' }),
          status: z.string().optional(),
        }),
        z.object({
          type: z.literal('new'),
          name: z.string().min(2, { message: 'Campaign name must be at least 2 characters.' }),
          objective: campaignObjectives,
          status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
        }),
      ]),
      adSet: z
        .discriminatedUnion('type', [
          z.object({
            type: z.literal('existing'),
            id: z.string().min(1, { message: 'Please select an ad set.' }),
            status: z.string().optional(),
          }),
          adSetNewBase,
        ])
        .refine(
          (data) => {
            if (data.type === 'new') {
              const hasDailyBudget = data.dailyBudget !== undefined;
              const hasLifetimeBudget = data.lifetimeBudget !== undefined;
              return (hasDailyBudget && !hasLifetimeBudget) || (!hasDailyBudget && hasLifetimeBudget);
            }
            return true;
          },
          {
            message: 'Either dailyBudget or lifetimeBudget must be set, but not both',
            path: ['lifetimeBudget'],
          },
        )
        .refine(
          (data) => {
            if (data.type === 'new') {
              const hasDailyBudget = data.dailyBudget !== undefined;
              const hasLifetimeBudget = data.lifetimeBudget !== undefined;
              return (hasDailyBudget && !hasLifetimeBudget) || (!hasDailyBudget && hasLifetimeBudget);
            }
            return true;
          },
          {
            message: 'Either dailyBudget or lifetimeBudget must be set, but not both',
            path: ['dailyBudget'],
          },
        )
        .refine(
          (data) => {
            if (data.type === 'new') {
              const hasLifetimeBudget = data.lifetimeBudget !== undefined;
              const missingStopTime = hasLifetimeBudget && !data.stopTime;
              return !missingStopTime;
            }
            return true;
          },
          {
            message: 'Stop time must be set if lifetimeBudget is set',
            path: ['stopTime'],
          },
        )
        .refine(
          (data) => {
            if (data.type === 'new') {
              const invalidTime =
                data.startTime &&
                data.stopTime &&
                (data.startTime > data.stopTime ||
                  Number(data.stopTime) - Number(data.startTime) < 24 * 60 * 60 * 1000);
              return !invalidTime;
            }
            return true;
          },
          {
            message: 'Start time must be 24 hours before stop time',
            path: ['startTime', 'stopTime'],
          },
        )
        .refine(
          (data) => {
            if (data.type === 'new') {
              // Meta API requires promoted_object for these optimization goals
              if (data.optimizationGoal === 'OFFSITE_CONVERSIONS') {
                // OFFSITE_CONVERSIONS requires pixelId and customEventType
                const hasPromotedObject = data.promotedObject && data.promotedObject.pixelId;
                const hasCustomEventType = data.promotedObject && data.promotedObject.customEventType;
                return hasPromotedObject && hasCustomEventType;
              } else if (data.optimizationGoal === 'LINK_CLICKS' || data.optimizationGoal === 'LEAD_GENERATION') {
                // LINK_CLICKS and LEAD_GENERATION require pixelId
                const hasPromotedObject = data.promotedObject && data.promotedObject.pixelId;
                return hasPromotedObject;
              } else if (data.optimizationGoal === 'APP_INSTALLS') {
                // APP_INSTALLS requires appId
                const hasAppId = data.promotedObject && data.promotedObject.appId;
                return hasAppId;
              }
            }
            return true;
          },
          {
            message:
              'Conversion tracking fields are required for OFFSITE_CONVERSIONS, LINK_CLICKS, APP_INSTALLS, and LEAD_GENERATION optimization goals',
            path: ['optimizationGoal'],
          },
        ),
      // Add will always be new
      ad: z.object({
        name: z.string().min(2, { message: 'Ad name must be at least 2 characters.' }),
        status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
        callToActionType: z.enum([
          'ADD_TO_CART',
          'APPLY_NOW',
          'ASK_ABOUT_SERVICES',
          'ASK_FOR_MORE_INFO',
          'AUDIO_CALL',
          'BOOK_A_CONSULTATION',
          'BOOK_NOW',
          'BOOK_TRAVEL',
          'BUY',
          'BUY_NOW',
          'BUY_TICKETS',
          'BUY_VIA_MESSAGE',
          'CALL',
          'CALL_ME',
          'CALL_NOW',
          'CHAT_WITH_US',
          'CONFIRM',
          'CONTACT',
          'CONTACT_US',
          'DONATE',
          'DONATE_NOW',
          'DOWNLOAD',
          'EVENT_RSVP',
          'FIND_A_GROUP',
          'FIND_YOUR_GROUPS',
          'FOLLOW_NEWS_STORYLINE',
          'FOLLOW_PAGE',
          'FOLLOW_USER',
          'GET_A_QUOTE',
          'GET_DIRECTIONS',
          'GET_IN_TOUCH',
          'GET_OFFER',
          'GET_OFFER_VIEW',
          'GET_PROMOTIONS',
          'GET_QUOTE',
          'GET_SHOWTIMES',
          'GET_STARTED',
          'INQUIRE_NOW',
          'INSTAGRAM_MESSAGE',
          'INSTALL_APP',
          'INSTALL_MOBILE_APP',
          'JOIN_CHANNEL',
          'LEARN_MORE',
          'LIKE_PAGE',
          'LISTEN_MUSIC',
          'LISTEN_NOW',
          'MAKE_AN_APPOINTMENT',
          'MESSAGE_PAGE',
          'MOBILE_DOWNLOAD',
          'NO_BUTTON',
          'OPEN_INSTANT_APP',
          'OPEN_LINK',
          'ORDER_NOW',
          'PAY_TO_ACCESS',
          'PLAY_GAME',
          'PLAY_GAME_ON_FACEBOOK',
          'PURCHASE_GIFT_CARDS',
          'RAISE_MONEY',
          'RECORD_NOW',
          'REFER_FRIENDS',
          'REQUEST_TIME',
          'SAY_THANKS',
          'SEE_MORE',
          'SELL_NOW',
          'SEND_A_GIFT',
          'SEND_GIFT_MONEY',
          'SEND_UPDATES',
          'SHARE',
          'SHOP_NOW',
          'SIGN_UP',
          'SOTTO_SUBSCRIBE',
          'START_ORDER',
          'SUBSCRIBE',
          'SWIPE_UP_PRODUCT',
          'SWIPE_UP_SHOP',
          'UPDATE_APP',
          'USE_APP',
          'USE_MOBILE_APP',
          'VIDEO_ANNOTATION',
          'VIDEO_CALL',
          'VIEW_CART',
          'VIEW_CHANNEL',
          'VIEW_INSTAGRAM_PROFILE',
          'VIEW_IN_CART',
          'VIEW_PRODUCT',
          'VISIT_PAGES_FEED',
          'WATCH_LIVE_VIDEO',
          'WATCH_MORE',
          'WATCH_VIDEO',
          'WHATSAPP_MESSAGE',
          'WOODHENGE_SUPPORT',
        ]),
        identityDisplayMode: z.enum(['CREATOR_AND_BRAND', 'CREATOR_ONLY', 'DYNAMIC']).default('CREATOR_AND_BRAND'),
        callToActionUrl: urlSchema.optional(),
        useUtmParameters: z.boolean().optional().default(false),
        utmParameters: z
          .object({
            utmSource: z
              .string()
              .optional()
              .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
                message: "UTM Source cannot contain '#', '=' or space characters",
              }),
            utmMedium: z
              .string()
              .optional()
              .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
                message: "UTM Medium cannot contain '#', '=' or space characters",
              }),
            utmCampaign: z
              .string()
              .optional()
              .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
                message: "UTM Campaign cannot contain '#', '=' or space characters",
              }),
            utmContent: z
              .string()
              .optional()
              .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
                message: "UTM Content cannot contain '#', '=' or space characters",
              }),
            utmTerm: z
              .string()
              .optional()
              .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
                message: "UTM Term cannot contain '#', '=' or space characters",
              }),
          })
          .optional(),
        customUtmParameters: z
          .array(
            z.object({
              key: z.string().optional(),
              value: z.string().optional(),
            }),
          )
          .optional(),
      }),
    })
    .superRefine((val, ctx) => {
      if (val.adSet?.type === 'existing' && val.campaign.type !== 'existing') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'You can only select an existing Ad Set if you have selected an existing Campaign.',
          path: ['adSet', 'type'],
        });
      }

      // Validate optimization goal and billing event compatibility
      if (val.adSet?.type === 'new' && val.campaign.type === 'new') {
        const campaignObjective = val.campaign.objective;
        const optimizationGoal = val.adSet.optimizationGoal;
        const billingEvent = val.adSet.billingEvent;

        // Define valid optimization goals for each campaign objective
        const validOptimizationGoals: Record<string, string[]> = {
          OUTCOME_AWARENESS: ['REACH', 'IMPRESSIONS', 'AD_RECALL_LIFT'],
          OUTCOME_TRAFFIC: ['LINK_CLICKS', 'LANDING_PAGE_VIEWS'],
          OUTCOME_ENGAGEMENT: ['POST_ENGAGEMENT', 'EVENT_RESPONSES', 'VIDEO_VIEWS'],
          OUTCOME_LEADS: ['LEAD_GENERATION', 'OFFSITE_CONVERSIONS'],
          OUTCOME_SALES: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS'],
          OUTCOME_APP_PROMOTION: ['APP_INSTALLS'],
        };

        // Define valid billing events for each optimization goal
        const validBillingEvents: Record<string, string[]> = {
          REACH: ['IMPRESSIONS'],
          IMPRESSIONS: ['IMPRESSIONS'],
          AD_RECALL_LIFT: ['IMPRESSIONS'],
          LANDING_PAGE_VIEWS: ['IMPRESSIONS'],
          POST_ENGAGEMENT: ['IMPRESSIONS'],
          EVENT_RESPONSES: ['IMPRESSIONS'],
          VIDEO_VIEWS: ['IMPRESSIONS'],
          LEAD_GENERATION: ['IMPRESSIONS'],
          OFFSITE_CONVERSIONS: ['IMPRESSIONS'],
          APP_INSTALLS: ['IMPRESSIONS'],
          LINK_CLICKS: ['LINK_CLICKS', 'IMPRESSIONS'],
        };

        // Validate optimization goal against campaign objective
        if (optimizationGoal && campaignObjective) {
          const validGoals = validOptimizationGoals[campaignObjective];
          if (validGoals && !validGoals.includes(optimizationGoal)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Optimization goal "${optimizationGoal}" is not valid for campaign objective "${campaignObjective}".`,
              path: ['adSet', 'optimizationGoal'],
            });
          }
        }

        // Validate billing event against optimization goal
        if (billingEvent && optimizationGoal) {
          const validEvents = validBillingEvents[optimizationGoal];
          if (validEvents && !validEvents.includes(billingEvent)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Billing event "${billingEvent}" is not valid for optimization goal "${optimizationGoal}".`,
              path: ['adSet', 'billingEvent'],
            });
          }
        }

        // EPD-2016: Require optimization goal for Sales campaigns
        if (campaignObjective === 'OUTCOME_SALES' && !optimizationGoal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Optimization goal is required for Sales campaigns. Please select OFFSITE_CONVERSIONS or LINK_CLICKS.',
            path: ['adSet', 'optimizationGoal'],
          });
        }
      }

      // EPD-1990: Require callToActionUrl for all call-to-action types including NO_BUTTON
      // Skip this check when website conversion is selected (handled by specific validation below)
      const isWebsiteConversion =
        val.adSet?.type === 'new' && 'conversionLocation' in val.adSet && val.adSet.conversionLocation === 'website';

      if (!isWebsiteConversion && (!val.ad.callToActionUrl || val.ad.callToActionUrl.trim() === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid URL for the Call To Action.',
          path: ['ad', 'callToActionUrl'],
        });
      }

      // CTA validation based on feature flag and campaign objective
      if (val.campaign.type === 'new') {
        const campaignObjective = val.campaign.objective;

        if (conversionObjectivesEnabled) {
          // New schema: Enhanced CTA validation based on campaign objective
          if (campaignObjective === 'OUTCOME_SALES') {
            // For Sales campaigns, validate against Sales CTA options
            if (!isSalesCampaignCtaOption(val.ad.callToActionType)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'This call to action is not available for Sales campaigns. Please select a valid option from the restricted list.',
                path: ['ad', 'callToActionType'],
              });
            }
          } else {
            // For other campaigns, validate against Awareness CTA options
            if (!isAwarenessCampaignCtaOption(val.ad.callToActionType)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'This call to action is not available. Please select a valid option from the restricted list.',
                path: ['ad', 'callToActionType'],
              });
            }
          }

          // EPD-2014: Require destination URL when website conversion is selected
          if (
            val.adSet?.type === 'new' &&
            'conversionLocation' in val.adSet &&
            val.adSet.conversionLocation === 'website'
          ) {
            if (!val.ad.callToActionUrl || val.ad.callToActionUrl.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Destination URL is required when website conversion is selected.',
                path: ['ad', 'callToActionUrl'],
              });
            }
          }
        } else {
          // Old schema: Basic CTA validation for Awareness campaigns only
          if (!isAwarenessCampaignCtaOption(val.ad.callToActionType)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'This call to action is not available. Please select a valid option from the restricted list.',
              path: ['ad', 'callToActionType'],
            });
          }
        }
      }
    })
    .refine(
      (data) => {
        // Check if campaign objective supports conversion goals for OFFSITE_CONVERSIONS
        if (data.adSet?.type === 'new' && data.adSet.optimizationGoal === 'OFFSITE_CONVERSIONS') {
          const campaignObjective = data.campaign?.type === 'new' ? data.campaign.objective : undefined;
          const supportsConversionGoals =
            campaignObjective === 'OUTCOME_SALES' || campaignObjective === 'OUTCOME_LEADS';
          return supportsConversionGoals;
        }
        return true;
      },
      {
        message:
          'OFFSITE_CONVERSIONS optimization goal requires a sales-focused campaign objective (OUTCOME_SALES or OUTCOME_LEADS)',
        path: ['adSet', 'optimizationGoal'],
      },
    );
};

// Export the default schema (for backward compatibility)
export const boostPostSchema = createBoostPostSchema(false);

// Export a type that works with both schemas
export type BoostPostFormValues = z.infer<ReturnType<typeof createBoostPostSchema>>;
